import axios from 'axios';
import rateLimiter from './rateLimiter';
import cacheManager from './cacheManager';
import { notifyUnauthorized } from './authEvents';

class APIClientV2 {
  constructor(baseURL = '', options = {}) {
    this.baseURL = baseURL || process.env.REACT_APP_API_BASE_URL || '';
    this.timeout = options.timeout || 30000;
    this.retryAttempts = options.retryAttempts || 3;
    this.retryDelay = options.retryDelay || 1000;
    this.cacheDuration = options.cacheDuration || 5 * 60 * 1000;

    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: this.timeout,
      headers: {
        'Content-Type': 'application/json',
      },
      paramsSerializer: {
        indexes: null, // Use repeat format (filter=a&filter=b)
      }
    });

    this._setupInterceptors();
  }

  _setupInterceptors() {
    this.client.interceptors.request.use(
      (config) => config,
      (error) => {
        console.error('API Request Error:', error);
        return Promise.reject(error);
      }
    );

    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const { config, response } = error;

        // The session ended or never started: back to the sign-in page
        if (response?.status === 401) {
          notifyUnauthorized();
          return Promise.reject(error);
        }

        console.error(`API Error: ${config?.url}`, {
          status: response?.status,
          message: error.message,
          data: response?.data,
        });

        if (config && !config.__retryCount) {
          config.__retryCount = 0;
        }

        if (
          config &&
          config.__retryCount < this.retryAttempts &&
          (!response || response.status >= 500)
        ) {
          config.__retryCount += 1;
          console.log(`Retrying request (${config.__retryCount}/${this.retryAttempts}): ${config.url}`);

          await new Promise(resolve => setTimeout(resolve, this.retryDelay));
          return this.client(config);
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * Search Contacts - /api/v2/contacts
   */
  async searchContacts(params = {}) {
    return this._request('GET', '/api/v2/contacts', { params });
  }

  /**
   * Search Companies - /api/v2/companies
   */
  async searchCompanies(params = {}) {
    return this._request('GET', '/api/v2/companies', { params });
  }

  /**
   * Search Positions - /api/v2/positions
   */
  async searchPositions(params = {}) {
    return this._request('GET', '/api/v2/positions', { params });
  }

  /**
   * Get Company Position Analytics - /api/v2/companies/{company_id}/positions
   */
  async getCompanyPositionAnalytics(companyId) {
    return this._request('GET', `/api/v2/companies/${companyId}/positions`);
  }

  /**
   * Get Database Stats - /api/v2/stats
   */
  async getDatabaseStats() {
    return this._request('GET', '/api/v2/stats');
  }

  /**
   * Health Check - /api/v2/health
   */
  async healthCheck() {
    return this._request('GET', '/api/v2/health');
  }

  /**
   * List Industries - /api/v2/search/industries
   */
  async listIndustries(params = {}) {
    return this._request('GET', '/api/v2/search/industries', { params });
  }

  /**
   * Search Contacts - /api/v2/search/contacts
   */
  async searchContactsV2(params = {}) {
    return this._request('GET', '/api/v2/search/contacts', { params });
  }

  /**
   * Search Companies - /api/v2/search/companies
   */
  async searchCompaniesV2(params = {}) {
    return this._request('GET', '/api/v2/search/companies', { params });
  }

  /**
   * Search Positions - /api/v2/search/positions
   */
  async searchPositionsV2(params = {}) {
    return this._request('GET', '/api/v2/search/positions', { params });
  }

  /**
   * Search Industry Company Position People - /api/v2/search/industry-company-position-people
   */
  async searchIndustryCompanyPositionPeople(params = {}) {
    return this._request('GET', '/api/v2/search/industry-company-position-people', { params });
  }

  /**
   * Search Industry Company - /api/v2/search/industry-company
   */
  async searchIndustryCompany(params = {}) {
    return this._request('GET', '/api/v2/search/industry-company', { params });
  }

  /**
   * Search Company People - /api/v2/search/company-people
   */
  async searchCompanyPeople(params = {}) {
    return this._request('GET', '/api/v2/search/company-people', { params });
  }

  /**
   * Search Company Position People - /api/v2/search/company-position-people
   */
  async searchCompanyPositionPeople(params = {}) {
    return this._request('GET', '/api/v2/search/company-position-people', { params });
  }

  /**
   * Get Roles - /api/v2/search/roles
   */
  async getRoles(params = {}) {
    return this._request('GET', '/api/v2/search/roles', { params });
  }

  /**
   * List Advisor Sources - /api/v2/contacts/sources
   */
  async listAdvisorSources() {
    return this._request('GET', '/api/v2/contacts/sources');
  }

  /**
   * Get Company Logo - /api/v2/logos/{logo_uuid}
   */
  async getCompanyLogo(logoUuid) {
    return this._request('GET', `/api/v2/logos/${logoUuid}`);
  }

  /**
   * Get Logo Info - /api/v2/logos/{logo_uuid}/info
   */
  async getLogoInfo(logoUuid) {
    return this._request('GET', `/api/v2/logos/${logoUuid}/info`);
  }

  /**
   * Get Cache Stats - /api/v2/logos/cache/stats
   */
  async getCacheStats() {
    return this._request('GET', '/api/v2/logos/cache/stats');
  }

  /**
   * Clear Logo Cache - /api/v2/logos/cache/{logo_uuid}
   */
  async clearLogoCache(logoUuid) {
    return this._request('DELETE', `/api/v2/logos/cache/${logoUuid}`);
  }

  /**
   * Generic request method with caching and rate limiting
   */
  async _request(method, url, config = {}) {
    const cacheKey = `${method}:${url}:${JSON.stringify(config.params || {})}`;

    if (method === 'GET') {
      const cached = cacheManager.get(cacheKey);
      if (cached) {
        console.log(`Cache hit for ${url}`);
        return cached;
      }
    }

    await rateLimiter.throttle();

    try {
      const response = await this.client.request({
        method,
        url,
        ...config
      });

      if (method === 'GET' && response.status === 200) {
        cacheManager.set(cacheKey, response.data, this.cacheDuration);
      }

      return response.data;
    } catch (error) {
      throw this._handleApiError(error, `Failed to ${method} ${url}`);
    }
  }

  _handleApiError(error, message) {
    const { response, request, config } = error;

    if (response) {
      const errorInfo = {
        message,
        status: response.status,
        statusText: response.statusText,
        data: response.data,
        url: config?.url,
        method: config?.method
      };

      console.error('API Error Response:', errorInfo);

      const enhancedError = new Error(`${message}: ${response.status} ${response.statusText}`);
      enhancedError.status = response.status;
      enhancedError.data = response.data;
      enhancedError.originalError = error;

      return enhancedError;
    } else if (request) {
      console.error('API Network Error:', { message, url: config?.url });
      const networkError = new Error(`${message}: Network error`);
      networkError.isNetworkError = true;
      networkError.originalError = error;
      return networkError;
    } else {
      console.error('API Setup Error:', { message, error: error.message });
      const setupError = new Error(`${message}: ${error.message}`);
      setupError.originalError = error;
      return setupError;
    }
  }

  /**
   * Build filter parameters according to V2 API spec
   */
  buildFilterParams(filters = []) {
    return filters.map(filter => {
      const { field, operator, value } = filter;
      return `${field}:${operator}:${value}`;
    });
  }

  /**
   * Convert old filter format to V2 API format
   */
  convertFiltersToV2(oldFilters = []) {
    return oldFilters.map(filter => {
      const operatorMap = {
        'contains': 'contains',
        'equals': 'equals',
        'starts_with': 'starts_with',
        'ends_with': 'ends_with',
        'gt': 'gt',
        'gte': 'gte',
        'lt': 'lt',
        'lte': 'lte'
      };

      return {
        field: filter.field,
        operator: operatorMap[filter.operator] || 'contains',
        value: filter.value
      };
    });
  }
}

class CachedAPIClientV2 extends APIClientV2 {
  constructor(baseURL, options = {}) {
    super(baseURL, {
      ...options,
      cacheDuration: options.cacheDuration || 10 * 60 * 1000,
    });
  }
}

class SecureAPIClientV2 extends CachedAPIClientV2 {
  constructor(baseURL, options = {}) {
    super(baseURL, options);
    
    this.client.defaults.headers.common['X-Client-Version'] = '2.0';
    this.client.defaults.headers.common['X-Request-Source'] = 'frontend-app';

    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }
}

export const apiClientV2 = new SecureAPIClientV2(
  process.env.REACT_APP_API_BASE_URL || '',
  {
    timeout: 30000,
    retryAttempts: 3,
    retryDelay: 1000,
    cacheDuration: 5 * 60 * 1000,
  }
);

export { APIClientV2, CachedAPIClientV2, SecureAPIClientV2 };
export default apiClientV2;
